import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Settings, X, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useCookieConsent, CONSENT_CATEGORIES } from "../contexts/CookieConsentContext";

/**
 * Spodní cookie lišta — zobrazí se při první návštěvě nebo po vyžádání z patičky.
 * Drží se moderního stylu (sliding bottom card, glass-morphism, gold accent),
 * nepřekrývá hlavní obsah víc, než je nutné, a na mobilu zaplňuje šíři.
 */
export default function CookieBanner() {
  const { bannerOpen, settingsOpen, setSettingsOpen, acceptAll, acceptEssentialsOnly } = useCookieConsent();
  const showBanner = bannerOpen && !settingsOpen;

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 200, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 200, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:left-6 md:left-auto md:max-w-[460px] z-[120]"
            data-testid="cookie-banner"
            role="region"
            aria-label="Souhlas s cookies"
          >
            <div className="rounded-2xl bg-apex-surface/95 backdrop-blur-md border border-apex-gold/30 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden">
              <div className="px-5 pt-5 pb-2 flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-apex-gold/15 text-apex-gold flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Cookie className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-apex-cream font-display font-semibold text-lg leading-tight">
                    Pár sušenek pro lepší zážitek
                  </h3>
                  <p className="text-sm text-apex-cream/75 mt-1.5 leading-relaxed">
                    Tento web ukládá pouze nezbytné údaje pro přihlášení. Pokud souhlasíte, načteme i mapu Google se studiem a zapamatujeme si, která upozornění jste už zavřeli.
                  </p>
                  <Link
                    to="/pravni/cookies"
                    className="inline-block mt-2 text-xs text-apex-gold hover:underline"
                    data-testid="cookie-banner-details-link"
                  >
                    Podrobnosti o cookies →
                  </Link>
                </div>
              </div>
              <div className="px-5 pb-5 pt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  onClick={() => setSettingsOpen(true)}
                  className="px-3 py-2.5 rounded-xl border border-apex-line text-apex-cream/85 hover:border-apex-gold/40 hover:text-apex-gold transition-colors text-sm font-semibold inline-flex items-center justify-center gap-1.5"
                  data-testid="cookie-banner-settings"
                >
                  <Settings className="w-3.5 h-3.5" /> Nastavení
                </button>
                <button
                  onClick={acceptEssentialsOnly}
                  className="px-3 py-2.5 rounded-xl border border-apex-line text-apex-cream/85 hover:border-apex-gold/40 hover:text-apex-cream transition-colors text-sm font-semibold"
                  data-testid="cookie-banner-essential-only"
                >
                  Pouze nezbytné
                </button>
                <button
                  onClick={acceptAll}
                  className="px-3 py-2.5 rounded-xl bg-apex-gold text-apex-bg hover:bg-apex-gold-soft transition-colors text-sm font-bold inline-flex items-center justify-center gap-1.5"
                  data-testid="cookie-banner-accept-all"
                >
                  <Check className="w-3.5 h-3.5" /> Přijmout vše
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CookiePreferencesModal />
    </>
  );
}

function CookiePreferencesModal() {
  const { consent, settingsOpen, setSettingsOpen, setCustomConsent } = useCookieConsent();

  // Local form state initialized from current persisted consent (or all-off).
  const [draft, setDraft] = useState(() => buildDraft(consent));

  // Sync the draft whenever the modal opens or the persisted consent changes
  useEffect(() => {
    if (settingsOpen) setDraft(buildDraft(consent));
  }, [settingsOpen, consent]);

  const toggle = (id) => {
    setDraft((d) => ({ ...d, [id]: !d[id] }));
  };

  return (
    <AnimatePresence>
      {settingsOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[130] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
          onClick={() => setSettingsOpen(false)}
          data-testid="cookie-settings-modal-backdrop"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-apex-surface w-full max-w-2xl rounded-t-3xl sm:rounded-3xl border border-apex-line shadow-2xl my-0 sm:my-8"
            data-testid="cookie-settings-modal"
          >
            <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4 border-b border-apex-line">
              <div>
                <h2 className="font-display text-2xl font-bold text-apex-cream">Nastavení cookies</h2>
                <p className="text-sm text-apex-cream/65 mt-1.5">Vy rozhodujete, co se na webu ukládá. Můžete to kdykoliv změnit v patičce.</p>
              </div>
              <button
                onClick={() => setSettingsOpen(false)}
                className="w-9 h-9 rounded-full bg-apex-card text-apex-cream/70 hover:text-apex-gold hover:bg-apex-bg flex items-center justify-center flex-shrink-0"
                aria-label="Zavřít"
                data-testid="cookie-settings-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {CONSENT_CATEGORIES.map((cat) => (
                <div
                  key={cat.id}
                  className="rounded-2xl border border-apex-line bg-apex-card p-4"
                  data-testid={`cookie-cat-${cat.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-apex-cream font-semibold">{cat.title}</h3>
                        {cat.locked && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-apex-gold/15 text-apex-gold">vždy aktivní</span>}
                      </div>
                      <p className="text-sm text-apex-cream/65 mt-1.5 leading-relaxed">{cat.description}</p>
                      {cat.examples && cat.examples !== "—" && (
                        <p className="text-[11px] font-mono text-apex-cream/45 mt-1.5 break-all">{cat.examples}</p>
                      )}
                    </div>
                    <Toggle
                      checked={cat.locked ? true : draft[cat.id]}
                      disabled={cat.locked}
                      onChange={() => toggle(cat.id)}
                      testId={`cookie-toggle-${cat.id}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 border-t border-apex-line flex flex-wrap gap-2 justify-end bg-apex-bg/50">
              <button
                onClick={() => setSettingsOpen(false)}
                className="px-4 py-2.5 rounded-xl text-apex-cream/75 hover:text-apex-cream text-sm font-semibold"
                data-testid="cookie-settings-cancel"
              >
                Zrušit
              </button>
              <button
                onClick={() => setCustomConsent(draft)}
                className="px-4 py-2.5 rounded-xl bg-apex-gold text-apex-bg hover:bg-apex-gold-soft text-sm font-bold inline-flex items-center gap-1.5"
                data-testid="cookie-settings-save"
              >
                <Check className="w-3.5 h-3.5" /> Uložit volbu
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function buildDraft(consent) {
  return {
    essential: true,
    functional: Boolean(consent?.functional),
    analytics: Boolean(consent?.analytics),
    marketing: Boolean(consent?.marketing),
  };
}

function Toggle({ checked, disabled, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange()}
      disabled={disabled}
      className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
        disabled
          ? "bg-apex-gold/40 cursor-not-allowed"
          : checked
          ? "bg-apex-gold"
          : "bg-apex-bg border border-apex-line"
      }`}
      role="switch"
      aria-checked={checked}
      data-testid={testId}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-apex-cream transition-all ${
          checked ? "left-[26px] bg-apex-bg" : "left-0.5 bg-apex-cream/85"
        }`}
      />
    </button>
  );
}
